"use client";

import { Languages } from "lucide-react";
import { usePathname } from "next/navigation";
import type { MouseEvent } from "react";
import { localePrefix, localeName, type Locale } from "@/lib/i18n/config";
import { LOCALE_COOKIE, LOCALE_COOKIE_MAX_AGE } from "@/lib/i18n/negotiate";
import { availableLocales, localePath } from "@/lib/i18n/routes";

/**
 * Links to the current page in every other language it exists in. Each language is its own root
 * layout, so these are plain document navigations; the query string (e.g. ?items= of a
 * comparison) and the fragment travel with them.
 */
export function LanguageSwitcher({ locale, title, className }: { locale: Locale; label?: string; title: string; className?: string }) {
  const pathname = usePathname() ?? (localePrefix[locale] || "/");
  const targets = availableLocales(pathname).filter(l => l !== locale);
  const keepState = (href: string, chosen: Locale) => (event: MouseEvent<HTMLAnchorElement>) => {
    // Remember the choice: the unprefixed homepage honours it before the browser's language.
    try { document.cookie = `${LOCALE_COOKIE}=${chosen}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`; } catch { /* cookies disabled */ }
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const { search, hash } = window.location;
    if (!search && !hash) return;
    event.preventDefault();
    window.location.assign(`${href}${search}${hash}`);
  };
  return <span className={className}>{targets.map(target => {
    const href = localePath(target, pathname);
    return <a key={target} href={href} hrefLang={target} lang={target} title={title} onClick={keepState(href, target)}><Languages size={14} aria-hidden="true" /> {localeName[target]}</a>;
  })}</span>;
}
